import React, { useState, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { getBottomOffsetAboveTabBar } from '../../utils/mobileTabNavigation';
import Text from '../common/Text';
import ColorCombinationCard from './ColorCombinationCard';
import PageHero from './PageHero';
import colorCombinationsData from '../../data/colorCombinations.json';

type Combination = { name: string; colors: string[] };

const allCombinations: Combination[] = (
  colorCombinationsData as { combinations: Combination[] }
).combinations;

export const COLOUR_LIBRARY_COUNT = allCombinations.length;

interface ColourLibraryContentProps {
  /** Optional container style (e.g. for in-app padding) */
  style?: object;
  /** Lift copy toast above the fixed bottom tab bar (in-app Library tab). */
  aboveBottomTabBar?: boolean;
}

const ColourLibraryContent: React.FC<ColourLibraryContentProps> = ({
  style,
  aboveBottomTabBar = false,
}) => {
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();
  const insets = useSafeAreaInsets();
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allCombinations;
    return allCombinations.filter((c) => c.name.toLowerCase().includes(q));
  }, [search]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  };

  const handleCopyColor = async (hex: string) => {
    try {
      if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        navigator.clipboard
      ) {
        await navigator.clipboard.writeText(hex);
        showToast(`Copied ${hex}`);
      } else {
        const Clipboard = require('@react-native-clipboard/clipboard').default;
        if (Clipboard?.setString) {
          Clipboard.setString(hex);
          showToast(`Copied ${hex}`);
        } else {
          Alert.alert('Copy', hex);
        }
      }
    } catch {
      Alert.alert('Copy', hex);
    }
  };

  const handleCopyAll = async (text: string) => {
    try {
      if (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        navigator.clipboard
      ) {
        await navigator.clipboard.writeText(text);
        showToast('Combination copied!');
      } else {
        const Clipboard = require('@react-native-clipboard/clipboard').default;
        if (Clipboard?.setString) {
          Clipboard.setString(text);
          showToast('Combination copied!');
        } else {
          Alert.alert('Copy', text);
        }
      }
    } catch {
      Alert.alert('Copy', text);
    }
  };

  const useGrid = !isMobile && Platform.OS === 'web';
  const toastBottom = aboveBottomTabBar
    ? getBottomOffsetAboveTabBar(insets.bottom)
    : 28 + insets.bottom;

  return (
    <View
      style={[styles.container, { backgroundColor: colors.background }, style]}
    >
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          !isMobile && styles.contentWide,
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <PageHero
          eyebrow="Sanzo Wada Inspired"
          title="Colour Library"
          subtitle={`${allCombinations.length} curated palettes · tap any swatch to copy its hex`}
        >
          <View
            style={[
              styles.searchBar,
              {
                backgroundColor: colors.card,
                borderColor: colors.border,
              },
            ]}
          >
            <Ionicons
              name="search-outline"
              size={17}
              color={colors.secondary}
              style={styles.searchIcon}
            />
            <TextInput
              style={[
                styles.searchInput,
                { color: colors.text },
                Platform.OS === 'web' && ({ outlineStyle: 'none' } as object),
              ]}
              placeholder="Search palettes…"
              placeholderTextColor={colors.secondary}
              value={search}
              onChangeText={setSearch}
              returnKeyType="search"
              autoCorrect={false}
              autoCapitalize="none"
            />
            {search.length > 0 && (
              <TouchableOpacity
                onPress={() => setSearch('')}
                style={styles.clearBtn}
                activeOpacity={0.7}
              >
                <Ionicons
                  name="close-circle"
                  size={17}
                  color={colors.secondary}
                />
              </TouchableOpacity>
            )}
          </View>
          {search.length > 0 && (
            <Text style={[styles.resultCount, { color: colors.secondary }]}>
              {filtered.length} palette
              {filtered.length !== 1 ? 's' : ''} found
            </Text>
          )}
        </PageHero>

        <View
          style={[
            styles.list,
            isMobile && styles.listMobile,
            useGrid && styles.listGrid,
          ]}
        >
          {filtered.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons
                name="color-palette-outline"
                size={48}
                color={colors.secondary}
                style={{ opacity: 0.4, marginBottom: 16 }}
              />
              <Text style={[styles.emptyTitle, { color: colors.text }]}>
                No palettes found
              </Text>
              <Text style={[styles.emptySubtitle, { color: colors.secondary }]}>
                Try a different search term
              </Text>
            </View>
          ) : (
            filtered.map((combo, index) => (
              <View
                key={`${combo.name}-${index}`}
                style={useGrid ? styles.gridItem : styles.listItem}
              >
                <ColorCombinationCard
                  name={combo.name}
                  colors={combo.colors}
                  onCopyColor={handleCopyColor}
                  onCopyAll={handleCopyAll}
                />
              </View>
            ))
          )}
        </View>
      </ScrollView>

      {toast && (
        <View
          style={[
            styles.toast,
            { backgroundColor: colors.text, bottom: toastBottom },
          ]}
        >
          <Text
            style={[
              styles.toastText,
              { color: colors.onPrimary ?? colors.background },
            ]}
          >
            {toast}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingBottom: 48,
  },
  contentWide: {
    maxWidth: 920,
    alignSelf: 'center',
    width: '100%',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 14,
    height: 46,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    height: '100%',
  },
  clearBtn: {
    padding: 4,
    marginLeft: 4,
  },
  resultCount: {
    fontSize: 13,
    marginTop: 6,
    fontWeight: '500',
  },
  list: {
    paddingHorizontal: 24,
  },
  listMobile: {
    paddingHorizontal: 16,
  },
  listGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
  },
  listItem: {},
  gridItem: {
    width: '50%',
    paddingHorizontal: 8,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 64,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 6,
  },
  emptySubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  toast: {
    position: 'absolute',
    alignSelf: 'center',
    zIndex: 1000,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 100,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 4px 20px rgba(0,0,0,0.2)' } as object)
      : { elevation: 6 }),
  },
  toastText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ColourLibraryContent;
